/**
 * Lazy loader for the Google Maps JavaScript API.
 *
 * Direct <script> tag injection — deliberately avoids any wrapper npm package
 * (no `@googlemaps/js-api-loader`, no `@react-google-maps/api`). The full
 * Maps JS bundle ships only when a caller awaits this loader, so it does not
 * count toward the SPA's gzip baseline.
 *
 * Decided 2026-05-06 as part of the Track D `feat/route-plan-google-maps`
 * fan-out. Customer-facing booking flow stays Maps-free per
 * `storage-spa/CLAUDE.md` Critical Rules; admin/logistics pages consume this
 * loader and gate on `REACT_APP_GOOGLE_MAPS_BROWSER_KEY` being set in the
 * deploy environment.
 */

declare global {
  interface Window {
    google?: typeof google;
    __kkGoogleMapsLoaderPromise?: Promise<typeof google>;
  }
}

/** Strongly-typed loader error so callers can render a friendly fallback. */
export class GoogleMapsLoaderError extends Error {
  readonly code: 'MISSING_KEY' | 'SCRIPT_FAILED' | 'TIMEOUT';

  constructor(code: 'MISSING_KEY' | 'SCRIPT_FAILED' | 'TIMEOUT', message: string) {
    super(message);
    this.code = code;
    this.name = 'GoogleMapsLoaderError';
  }
}

const SCRIPT_ID = 'kk-google-maps-loader';
const LOAD_TIMEOUT_MS = 15000;

/**
 * Resolve once the Google Maps JS API is ready. Idempotent: subsequent calls
 * reuse the same promise even across React component remounts.
 *
 * Rejects with a `GoogleMapsLoaderError` when:
 * - `REACT_APP_GOOGLE_MAPS_BROWSER_KEY` is unset (code: `MISSING_KEY`)
 * - the script tag fails to load (code: `SCRIPT_FAILED`)
 * - the script tag does not register `window.google.maps` within 15 s
 *   (code: `TIMEOUT`)
 */
export function loadGoogleMaps(): Promise<typeof google> {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new GoogleMapsLoaderError('SCRIPT_FAILED', 'Google Maps cannot load on the server.'),
    );
  }

  // Already loaded in this tab.
  if (window.google && window.google.maps) {
    return Promise.resolve(window.google);
  }

  // In-flight load (memoise across remounts).
  if (window.__kkGoogleMapsLoaderPromise) {
    return window.__kkGoogleMapsLoaderPromise;
  }

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY;
  if (!apiKey || !apiKey.trim()) {
    return Promise.reject(
      new GoogleMapsLoaderError(
        'MISSING_KEY',
        'REACT_APP_GOOGLE_MAPS_BROWSER_KEY is not set. Map view unavailable.',
      ),
    );
  }

  const promise = new Promise<typeof google>((resolve, reject) => {
    // Reuse any pre-existing tag (e.g. server-side hydration scenario).
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const callbackName = '__kkGoogleMapsCallback';

    const cleanup = () => {
      // Drop the global callback once the load resolves either way.
      try {
        delete (window as unknown as Record<string, unknown>)[callbackName];
      } catch {
        (window as unknown as Record<string, unknown>)[callbackName] = undefined;
      }
    };

    (window as unknown as Record<string, unknown>)[callbackName] = () => {
      cleanup();
      if (window.google && window.google.maps) {
        resolve(window.google);
      } else {
        reject(
          new GoogleMapsLoaderError(
            'SCRIPT_FAILED',
            'Google Maps callback fired but window.google.maps is missing.',
          ),
        );
      }
    };

    const timer = window.setTimeout(() => {
      cleanup();
      reject(
        new GoogleMapsLoaderError(
          'TIMEOUT',
          'Google Maps JS API did not load within 15 seconds.',
        ),
      );
    }, LOAD_TIMEOUT_MS);

    const handleError = () => {
      window.clearTimeout(timer);
      cleanup();
      reject(
        new GoogleMapsLoaderError('SCRIPT_FAILED', 'Failed to load the Google Maps JS API.'),
      );
    };

    if (existing) {
      // A previous attempt left a tag; re-attach listeners.
      existing.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    // `loading=async` matches Google's current best-practice and keeps the
    // main thread responsive while the bundle parses.
    script.src =
      `https://maps.googleapis.com/maps/api/js` +
      `?key=${encodeURIComponent(apiKey)}` +
      `&libraries=geometry` +
      `&loading=async` +
      `&callback=${callbackName}`;
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  });

  window.__kkGoogleMapsLoaderPromise = promise;
  // If the load fails, drop the cached rejection so a later mount can retry
  // (e.g. once an admin sets the key in pipelines and reloads).
  promise.catch(() => {
    window.__kkGoogleMapsLoaderPromise = undefined;
  });

  return promise;
}
